import { useMemo } from "react";
import {
  Control,
  Controller,
  ControllerProps,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import millify from "millify";

import { Box, FormHelperText, Slider, Typography } from "@mui/material";

import { formatCurrency } from "@covertree/utils";
import { OptionType } from "@covertree/types";

interface Props<TValues extends FieldValues> {
  disabled?: boolean;
  name: string;
  control: Control<TValues>;
  options: OptionType<number>[];
  step?: number | null;
  rules?: Pick<RegisterOptions, "validate">;
}

const buildSliderOptions = (options: OptionType<number>[]) => {
  return options.map((option, index) => {
    const isFirst = index === 0;
    const isLast = index === options.length - 1;
    return {
      value: Number(option.value),
      label:
        isFirst || isLast
          ? isNaN(Number(option.label))
            ? option.label
            : isFirst
              ? `from $${millify(Number(option.label), { precision: 0 })}`
              : `to $${millify(Number(option.label), { precision: 0 })}`
          : undefined,
    };
  });
};

export const FormCurrencySlider = <TValues extends FieldValues>({
  disabled,
  options,
  control,
  name,
  step = null,
  rules,
}: Props<TValues>) => {
  const { lastValueIndex, min, max } = useMemo(() => {
    const lastValueIndex = options.length - 1;
    return {
      lastValueIndex,
      min: options[0]?.value,
      max: options[lastValueIndex]?.value,
    };
  }, [options]);

  return (
    <Controller
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      name={name as any}
      control={control}
      rules={rules as ControllerProps<TValues>["rules"]}
      render={({ field, fieldState: { error } }) => {
        return (
          <Box>
            <Typography
              variant="h3"
              component="span"
              data-testid={`${name}-slider-value`}>
              {formatCurrency(field.value)}
            </Typography>
            <Box px={3}>
              <Slider
                step={step}
                min={min}
                max={max}
                marks={buildSliderOptions(options)}
                data-testid={`${name}-slider`}
                sx={(theme) => ({
                  [`& .MuiSlider-markLabel[data-index="${lastValueIndex}"]`]: {
                    transform: `translateX(-100%) translateX(${theme.spacing(3)})`,
                  },
                })}
                disabled={disabled}
                {...field}
              />
            </Box>
            {error?.message && (
              <Box mt={2}>
                <FormHelperText
                  error
                  data-testid={`${name}-slider-error`}
                  {...(!error.message && { sx: { margin: 0 } })}>
                  {error.message}
                </FormHelperText>
              </Box>
            )}
          </Box>
        );
      }}
    />
  );
};
